import { CSSProperties, FC, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { isEmpty } from 'lodash/fp';

import {
    Dropdown,
    DropdownProps,
    FieldLabel,
    FlexRow,
    FormGroup,
    Small,
} from '@portal/common/components';
import { YEAR_KEY } from '@portal/common/models/data-key';
import { styled } from '@portal/common/theme';
import { DataGranularityKey, DropdownOption, DropdownOptionGroup } from '@portal/common/types';
import { Size, matchSize } from '@portal/common/utility/common-props-helpers';
import {
    getLocalePrefixForGranularityKey,
    noneOfSelectedOptionsIsAllowedByGranularity,
} from '@portal/common/utility/filters-helpers';

import _ from '../../locale';
import { validateSelectedRefinementFiltersComplexityLimit } from '../../store/data-explorer/actions';
import {
    getNowcastYears,
    getSelectedConditionsDataTypesGranularity,
    getSelectedConditionsRefinedGranularity,
} from '../../store/data-explorer/selectors';
import { appendNowcastYearsToYearOptions } from '../../utility/data/nowcasts';
import {
    DataExplorerLayoutContextValueProps,
    useDataExplorerLayout,
} from '../DataExplorer/context';

const StyledFormGroup = styled(FormGroup)<{
    $size?: Size;
    $hasLabel?: boolean;
}>(({ $size, $hasLabel }) =>
    !$hasLabel
        ? {
              margin: 0,
              width: '100%',
          }
        : {
              marginBottom: matchSize({ sm: 12, md: 30 })($size),
          }
);

const FilterLabel = styled(FieldLabel)(() => ({
    display: 'inline',
}));

const RevertFilterLabel = styled(Small)(({ theme }) => ({
    textTransform: 'none',
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeight.roman,
    color: theme.color.gray70,
    fontSize: '1.3rem',
    paddingBottom: 4,
    opacity: 1,
}));

type OwnProps = {
    className?: string;
    label?: string;
    filterKey: DataGranularityKey;
    filterValue?: number[];
    defaultValue?: number[];
    isMulti?: boolean;
    isRevertToDefaultDisabled?: boolean;
    preprocessOptions?: (
        options: DropdownProps['options']
    ) => (DropdownOption | DropdownOptionGroup)[];
    onChange?: (value: DropdownOption[] | null) => void;
    granularity?: any;
    refinedGranularity?: any;
    disableStatusCheck?: boolean;
    skipRefinementFiltersUpdate?: boolean;
    options?: DropdownOption[];
    maxValuesAmount?: number | null;
    shouldIncludeNowcasts?: boolean;
    labelStyles?: CSSProperties;
    revertLabelStyles?: CSSProperties;
};

type Props = OwnProps &
    DataExplorerLayoutContextValueProps &
    Omit<
        DropdownProps,
        | 'options'
        | 'value'
        | 'valueMode'
        | 'status'
        | 'label'
        | 'dataTestid'
        | 'multiselectLabel'
        | 'optionLabelGetter'
        | 'optionDisabledGetter'
        | 'onSelect'
        | 'onMultiSelect'
        | 'onRemove'
        | 'onClear'
    >;

// TODO: extract refinement filter specific functionality into RefinementFilterDropdown
const ChartDropdown: FC<Props> = ({
    id,
    className,
    label,
    filterKey,
    filterValue,
    defaultValue,
    preprocessOptions,
    isMulti,
    labelStyles,
    revertLabelStyles,
    onChange,
    refinedGranularity: refinedGranularityProp,
    granularity: granularityProp,
    options,
    isRevertToDefaultDisabled,
    shouldIncludeNowcasts,
    isClearable,
    skipRefinementFiltersUpdate,
    maxValuesAmount,
    disableStatusCheck,
    ...restProps
}) => {
    const dispatch = useDispatch();

    const granularityFromStore = useSelector(getSelectedConditionsDataTypesGranularity);
    const refinedGranularityFromStore = useSelector(getSelectedConditionsRefinedGranularity);
    const nowcastYears = useSelector(getNowcastYears);
    const layoutProps = useDataExplorerLayout();

    const granularity = granularityProp || granularityFromStore;
    const refinedGranularity = refinedGranularityProp || refinedGranularityFromStore;

    const handleChange = useCallback(
        (value: null | DropdownOption[]) => {
            // if not clearable do not allow empty selection
            if (!isClearable && (Array.isArray(value) ? !value.length : value == null)) {
                return;
            }

            const formattedValue =
                Array.isArray(value) && value.length && maxValuesAmount
                    ? value.slice(-maxValuesAmount)
                    : value;

            if (!skipRefinementFiltersUpdate) {
                dispatch(
                    validateSelectedRefinementFiltersComplexityLimit({
                        [filterKey]: formattedValue,
                    })
                );
            }

            onChange?.(formattedValue);
        },
        [isClearable, maxValuesAmount, skipRefinementFiltersUpdate, dispatch, filterKey, onChange]
    );

    const handleSelect = useCallback(
        (ev: { target: { value: null | DropdownOption[] } }) => {
            handleChange(ev.target.value);
        },
        [handleChange]
    );

    const handleRemove = useCallback(
        (ev: { target: { value: null | DropdownOption[] } }) => {
            handleChange(ev.target.value);
        },
        [handleChange]
    );

    const handleClear = useCallback(
        (ev: { target: { value: null } }) => {
            handleChange(ev.target.value);
        },
        [handleChange]
    );

    const handleMultiSelect = useCallback(
        (ev: { target: { value: DropdownOption[] } }) => {
            handleChange(ev.target.value);
        },
        [handleChange]
    );

    const revertToDefault = useCallback(() => {
        handleChange(defaultValue);
    }, [handleChange, defaultValue]);

    const getStatus = useCallback(() => {
        if (disableStatusCheck || !refinedGranularity || !refinedGranularity[filterKey]) {
            return;
        }

        if (filterValue == null) {
            return 'error';
        }

        if (!Array.isArray(filterValue)) {
            console.error(
                'Use ChartDropdown only for RefinementFilters, as a legacy workaround please use disableStatusCheck but best you should refactor it.'
            );
            return;
        }

        if (
            noneOfSelectedOptionsIsAllowedByGranularity(
                { [filterKey]: filterValue },
                refinedGranularity
            )(filterKey)
        ) {
            return 'error';
        }
    }, [disableStatusCheck, refinedGranularity, filterKey, filterValue]);

    const { processedOptions, enabledOptions } = useMemo(() => {
        const shouldAppendNowcastYears =
            filterKey === YEAR_KEY && shouldIncludeNowcasts && !isEmpty(nowcastYears);

        let granularityOptions = options || granularity[filterKey] || [];
        if (shouldAppendNowcastYears) {
            granularityOptions = appendNowcastYearsToYearOptions(granularityOptions, nowcastYears);
        }

        const processedOptions = preprocessOptions
            ? preprocessOptions(granularityOptions)
            : granularityOptions;

        let enabledOptions = refinedGranularity && refinedGranularity[filterKey];
        if (shouldAppendNowcastYears && enabledOptions) {
            enabledOptions = appendNowcastYearsToYearOptions(enabledOptions, nowcastYears);
        }

        return { processedOptions, enabledOptions };
    }, [
        options,
        granularity,
        filterKey,
        shouldIncludeNowcasts,
        nowcastYears,
        preprocessOptions,
        refinedGranularity,
    ]);

    const dropdownValue = filterValue == null ? [] : filterValue;

    const showRevertToDefault =
        !isRevertToDefaultDisabled &&
        defaultValue &&
        filterValue &&
        defaultValue.toString() !== filterValue.toString();

    const localePrefix = getLocalePrefixForGranularityKey(filterKey);

    const optionLabelGetter = useCallback((o) => _.withPrefix(localePrefix, o), [localePrefix]);

    const optionDisabledGetter = useCallback(
        (o) => enabledOptions && !enabledOptions.includes(o),
        [enabledOptions]
    );

    const withLabel = !!(label || showRevertToDefault);

    return (
        <StyledFormGroup className={className} $size={layoutProps.size} $hasLabel={withLabel}>
            {withLabel && (
                <FlexRow justify="space-between">
                    {label && (
                        <FilterLabel style={labelStyles} htmlFor={id}>
                            {label}
                        </FilterLabel>
                    )}
                    {showRevertToDefault && (
                        <RevertFilterLabel style={revertLabelStyles}>
                            <a onClick={revertToDefault}>Revert</a> to Default
                        </RevertFilterLabel>
                    )}
                </FlexRow>
            )}
            <Dropdown
                id={id}
                dataTestid={id}
                multiselectLabel={isMulti ? _.get('multiselect_label') : undefined}
                valueMode={isMulti ? 'multiArray' : 'singleArray'}
                options={processedOptions}
                status={getStatus()}
                value={dropdownValue}
                optionLabelGetter={optionLabelGetter}
                optionDisabledGetter={optionDisabledGetter}
                onSelect={handleSelect}
                onMultiSelect={handleMultiSelect}
                onRemove={handleRemove}
                onClear={handleClear}
                {...restProps}
                {...layoutProps}
            />
        </StyledFormGroup>
    );
};

export default ChartDropdown;
